<template>
  <div data-test-id="legal-page" class="bg-background text-foreground h-dvh overflow-y-auto">
    <main class="mx-auto w-full max-w-3xl px-6 py-10">
      <div
        v-if="whiteLabelRef.logoSmall || whiteLabelRef.logo || whiteLabelRef.companyName"
        class="flex items-center gap-2 mb-8"
      >
        <img
          v-if="whiteLabelRef.logoSmall || whiteLabelRef.logo"
          :src="whiteLabelRef.logoSmall || whiteLabelRef.logo"
          alt="logo"
          class="!h-8 !w-8 shrink-0 rounded-md object-contain"
        />
        <span v-if="whiteLabelRef.companyName" class="text-foreground font-semibold truncate">
          {{ whiteLabelRef.companyName }}
        </span>
      </div>

      <div
        v-if="renderResult.missingVars.length > 0"
        data-test-id="legal-page-not-configured"
        class="py-16 text-center"
      >
        <h1 class="text-2xl font-semibold mb-3">{{ t('legal.notConfigured.title') }}</h1>
        <p class="text-muted-foreground">{{ t('legal.notConfigured.body') }}</p>
      </div>

      <template v-else>
        <p v-if="lastUpdatedRef" data-test-id="legal-page-last-updated" class="text-muted-foreground mb-6 text-sm">
          {{ t('legal.lastUpdated', { date: lastUpdatedRef }) }}
        </p>

        <article
          data-test-id="legal-page-content"
          class="text-[0.95rem] leading-relaxed [&_h1]:text-3xl [&_h1]:font-bold [&_h1]:leading-tight [&_h1]:mb-6 [&_h2]:text-xl [&_h2]:font-semibold [&_h2]:mt-10 [&_h2]:mb-4 [&_h2]:pb-2 [&_h2]:border-b [&_h2]:border-border [&_h3]:text-lg [&_h3]:font-semibold [&_h3]:mt-7 [&_h3]:mb-3 [&_p]:mb-4 [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:mb-4 [&_ol]:list-decimal [&_ol]:pl-6 [&_ol]:mb-4 [&_li]:mb-2 [&_strong]:font-semibold [&_em]:italic [&_a]:text-primary [&_a]:underline [&_a]:break-words [&_hr]:my-10 [&_hr]:border-border"
        >
          <LegalMarkdownContent :html="renderResult.html" />
        </article>
      </template>
    </main>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'

import LegalMarkdownContent from '@/features/legal/components/legal-markdown-content/legal-markdown-content.vue'
import { renderLegalDoc } from '@/features/legal/lib/render'
import useWhiteLabel from '@/hooks/use-white-label.hook'
import { useI18n } from '@/lib/i18n'

const props = defineProps<{
  template: string
  /** KC-538 : nom du document, pour afficher sa date de dernière mise à jour */
  document?: 'cgu' | 'privacy'
}>()

const { t, locale } = useI18n()
const { whiteLabelRef } = useWhiteLabel()

const renderResult = computed(() =>
  renderLegalDoc(props.template, {
    platformUrl: window.location.origin,
    rt: whiteLabelRef.value.rt,
  }),
)

// KC-538 : la version du document (date ISO, cf. LEGAL_DOC_VERSIONS côté API)
// est affichée comme date de dernière mise à jour, localisée. Ligne masquée si
// la version n'est pas disponible ou pas parsable.
const lastUpdatedRef = computed(() => {
  if (!props.document) return null
  const version = whiteLabelRef.value.legalDocVersions?.[props.document]
  if (!version) return null
  const parsed = new Date(version)
  if (Number.isNaN(parsed.getTime())) return null
  return parsed.toLocaleDateString(String(locale) === 'en' ? 'en-US' : 'fr-FR', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  })
})
</script>
